import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { IconButton } from 'components/buttons/IconButton';
import { ContextMenu } from '.';

export type IHeaderSubAction = { name: string; action: () => void };

export interface IHeaderActionProps {
  iconClassName: string;
  title?: string;
  disabled?: boolean;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  subActions?: IHeaderSubAction[] | (() => Promise<IHeaderSubAction[]>);
  onShowSubActions: () => void;
  onHideSubActions: () => void;
}

export function HeaderAction({
  title,
  disabled,
  className,
  iconClassName,
  subActions: subActionsProp,
  onClick,
  onShowSubActions,
  onHideSubActions,
}: IHeaderActionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [subActions, setSubActions] = useState<IHeaderSubAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const showActions = useCallback(async () => {
    setIsOpen(true);
    onShowSubActions();

    try {
      setIsLoading(true);
      const actions = typeof subActionsProp === 'function' ? await subActionsProp() : subActionsProp;
      actions && setSubActions(actions);
    } finally {
      setIsLoading(false);
    }
  }, [onShowSubActions, subActionsProp]);

  const hideSubActions = useCallback(() => {
    setIsOpen(false);
    setSubActions([]);
    onHideSubActions();
  }, [onHideSubActions]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      } else if (subActionsProp) {
        showActions();
      } else {
        console.warn('Neither onClick nor subActions were received.');
      }
    },
    [onClick, showActions, subActionsProp]
  );

  return (
    <>
      <IconButton
        className={classNames(className, 'tt-header-actions__action')}
        iconSize={'large'}
        iconClassName={iconClassName}
        title={title}
        disabled={disabled || isLoading}
        ref={buttonRef}
        onClick={handleClick}
        aria-label={title}
      />
      {!!subActions && (
        <ContextMenu
          isOpen={isOpen}
          onClose={hideSubActions}
          sourceRef={buttonRef}
          placement="bottom"
          isLoading={isLoading}
          actions={subActions.map(action => ({
            value: action.name,
            label: action.name,
            onClick: () => {
              action.action();
              hideSubActions();
            },
          }))}
        />
      )}
    </>
  );
}
